import type { HitlStatus } from "@/types/hitl";
import { cn } from "@/lib/utils";

const STATUS_STYLES: Record<string, string> = {
  pending: "border-warning/60 text-warning bg-warning/10",
  approved: "border-success/60 text-success bg-success/10",
  rejected: "border-destructive/60 text-destructive bg-destructive/10",
  changes_requested: "border-info/60 text-info bg-info/10",
};

function computeDeadlineClass(deadline?: string | null) {
  if (!deadline) return null;
  const timestamp = Date.parse(deadline);
  if (Number.isNaN(timestamp)) return null;
  const now = Date.now();
  if (timestamp < now) {
    return { label: "Overdue", className: "border-destructive/70 text-destructive bg-destructive/10" };
  }
  const hoursRemaining = (timestamp - now) / (1000 * 60 * 60);
  if (hoursRemaining <= 6) {
    return { label: "Due soon", className: "border-warning/70 text-warning bg-warning/10" };
  }
  return null;
}

export type HitlBadgeProps = {
  status?: HitlStatus | string | null;
  deadline?: string | null;
  className?: string;
  size?: "sm" | "md";
  showDeadline?: boolean;
};

export function HitlBadge({ status, deadline, className, size = "sm", showDeadline = true }: HitlBadgeProps) {
  if (!status) return null;
  const normalized = status.toLowerCase();
  const statusClass = STATUS_STYLES[normalized] ?? "border-border text-foreground/80 bg-muted/40";
  const baseClass =
    size === "md"
      ? "text-xs px-3 py-1"
      : "text-[11px] px-2 py-0.5";
  const deadlineState = showDeadline ? computeDeadlineClass(deadline) : null;
  return (
    <span className={cn("inline-flex items-center gap-2 rounded-full border uppercase tracking-wide", statusClass, baseClass, className)}>
      {normalized.replace("_", " ")}
      {deadlineState && (
        <span className={cn("rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide", deadlineState.className)}>
          {deadlineState.label}
        </span>
      )}
    </span>
  );
}

export default HitlBadge;
